import { lazy, Suspense, type CSSProperties } from 'react';
import { track } from '@/lib/telemetry';
import { Outlet } from 'react-router-dom';
import { RedirectToSignIn, SignedIn, SignedOut, useAuth, useClerk, useUser } from '@clerk/clerk-react';
import { AppSidebar } from '@/components/AppSidebar';
import { AppHeader } from '@/components/AppHeader';
import { AppFooter } from '@/components/AppFooter';
import { BottomNavBar } from '@/components/BottomNavBar';
import { PlatformOnboardingGate } from '@/components/onboarding/PlatformOnboardingGate';
import { ProblemReportDialog } from '@/components/problem-report/ProblemReportDialog';
import { PersonPropertiesSync } from '@/components/PersonPropertiesSync';
import { AppHeaderProvider } from '@/contexts/AppHeaderContext';
import { SendToSpaceProvider } from '@/components/spaces/SendToSpaceProvider';
import { SpaceScopeProvider, useSpaceScope } from '@/contexts/SpaceScopeContext';
import { ChatPageProvider } from '@/contexts/ChatPageContext';
import { useScrollResetOnNavigate } from '@/hooks/useScrollResetOnNavigate';
import { usePathname, useRouter, useSearchParams } from '@/lib/router';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';

// Chat pulls in shiki + streamdown + mermaid (~11 MB combined). Lazy-load
// so the dashboard shell renders without it.
const ChatDrawer = lazy(() => import('@/components/chat/ChatDrawer').then((m) => ({ default: m.ChatDrawer })));

// Personal Link must not imply it understands the active space. Space-scoped
// assistant support is introduced with the privacy boundary in VER-420.
// Keyed by the account: `SignedIn` stays mounted when Clerk switches between two
// signed-in accounts, and a remount is what keeps one person's transcript from
// rendering, even for a frame, under the next one's session.
function ScopedChatDrawer() {
  const { scope } = useSpaceScope();
  const { userId } = useAuth();
  return scope === 'personal' ? <ChatDrawer key={userId ?? 'anonymous'} /> : null;
}

export default function DashboardLayout() {
  const { signOut } = useClerk();
  const { user } = useUser();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Land every navigation at the top — the dashboard scroller is a persistent
  // inner element React Router never unmounts, so its offset otherwise carries over.
  useScrollResetOnNavigate();

  const displayName =
    user?.fullName ??
    user?.primaryEmailAddress?.emailAddress ??
    user?.username ??
    'Usuário';
  const userEmail = user?.primaryEmailAddress?.emailAddress;

  // Central sign-out: every path (sidebar, bottom nav, onboarding/blocking
  // overlays that call onLogout directly) is tracked here, exactly once.
  // No default surface — consumers may wire the callback straight into
  // onClick, and a defaulted first param would swallow the SyntheticEvent.
  const handleLogout = (surface: 'sidebar' | 'bottom_nav' | 'onboarding') => {
    track('action.auth.sign_out', { surface });
    void signOut({ redirectUrl: '/login' });
  };

  const isInvestmentDetail = /^\/investments\/[^/]+$/.test(pathname) && pathname !== '/investments/calendar';
  const isInvestmentCalendar = pathname === '/investments/calendar';
  const isGoalFormPage = pathname === '/metas/new' || /^\/metas\/[^/]+\/edit$/.test(pathname);
  const isInvoiceDetail =
    (pathname === '/invoices' || pathname === '/transactions/credit') &&
    searchParams.get('view') === 'invoice_detail';

  const showBackButton =
    isInvestmentDetail ||
    isInvestmentCalendar ||
    isGoalFormPage ||
    isInvoiceDetail;

  const handleBack = () => {
    router.back();
  };

  return (
    <>
      <SignedOut>
        <RedirectToSignIn redirectUrl="/" />
      </SignedOut>

      <SignedIn>
        <SpaceScopeProvider>
        <SendToSpaceProvider>
        <ChatPageProvider>
        <AppHeaderProvider>
          <PlatformOnboardingGate onLogout={() => handleLogout('onboarding')}>
            <SidebarProvider
              style={
                {
                  // Redesign shell widths: expanded card ~248px (276 − 28px inset),
                  // collapsed rail ~76px — matches the proposal density.
                  '--sidebar-width': '276px',
                  '--sidebar-width-icon': '74px',
                } as CSSProperties
              }
            >
              <AppSidebar
                onLogout={() => handleLogout('sidebar')}
                userName={displayName}
                userEmail={userEmail}
                userImageUrl={user?.imageUrl}
              />
              <SidebarInset
                data-dashboard-scroll
                className="h-svh overflow-x-hidden overflow-y-auto overscroll-y-none bg-background"
              >
                <AppHeader
                  showBackButton={showBackButton}
                  onBack={handleBack}
                  backLabel="Voltar"
                />
                <div
                  data-onboarding-dashboard
                  className="min-h-0 flex-1 p-4 pb-24 sm:p-8 md:pb-8"
                >
                  {/* The centralized content shell every screen inherits (the new
                      Home's treatment): edge-to-edge on normal/large screens,
                      capped only on ultra-wide monitors so the layout doesn't
                      stretch apart. */}
                  <div className="mx-auto w-full min-w-0 max-w-[1920px]">
                    <Outlet />
                    <AppFooter />
                  </div>
                </div>
                <BottomNavBar onLogout={() => handleLogout('bottom_nav')} />
              </SidebarInset>
              <Suspense fallback={null}>
                <ScopedChatDrawer />
              </Suspense>
              <ProblemReportDialog />
              <PersonPropertiesSync />
            </SidebarProvider>
          </PlatformOnboardingGate>
        </AppHeaderProvider>
        </ChatPageProvider>
        </SendToSpaceProvider>
        </SpaceScopeProvider>
      </SignedIn>
    </>
  );
}
